/**
 * Central admin role definitions for the EzSignly admin app.
 * Extend by adding new ROLE_IDS, PERMISSIONS, and ROLE_CONFIG entries.
 */

/** @typedef {'SUPER_ADMIN' | 'ADMIN' | 'SUPPORT' | 'SEO'} AdminRoleId */

export const ROLE_IDS = {
  SUPER_ADMIN: "SUPER_ADMIN",
  ADMIN: "ADMIN",
  SUPPORT: "SUPPORT",
  SEO: "SEO",
};

/**
 * Fine-grained permission keys — use for route checks and nav visibility.
 * Add new keys here when new areas are introduced.
 */
export const PERMISSIONS = {
  DASHBOARD: "dashboard",
  USERS_READ: "users.read",
  /** List Actions column, user detail login toggle — Admin+ only; Support is read-only on users. */
  USERS_ACTIONS: "users.actions",
  ORGANIZATIONS: "organizations",
  COMPANIES: "companies",
  BLOGS_EZ: "blogs.ezsignly",
  BLOG_CATEGORIES_EZ: "blogCategories.ezsignly",
  HAYSTACKERS_BLOGS: "blogs.haystackers",
  HAYSTACKERS_BLOG_CATEGORIES: "blogCategories.haystackers",
  SYSTEM_USERS: "systemUsers",
  SUPPORT_TICKETS: "supportTickets",
  COUPONS: "coupons",
  MIGRATIONS: "migrations",
};

/**
 * Human-readable role metadata + fixed permission sets (must stay in sync with backend).
 */
export const ROLE_CONFIG = {
  [ROLE_IDS.SUPER_ADMIN]: {
    label: "Super Admin",
    shortLabel: "Super Admin",
    description: "Full access to all admin features.",
    permissions: Object.values(PERMISSIONS),
    assignableBy: [],
  },
  [ROLE_IDS.ADMIN]: {
    label: "Admin",
    shortLabel: "Admin",
    description: "Full operational access except super-admin-only actions.",
    permissions: Object.values(PERMISSIONS),
    assignableBy: [ROLE_IDS.SUPER_ADMIN],
  },
  [ROLE_IDS.SUPPORT]: {
    label: "Support",
    shortLabel: "Support",
    description: "Support tickets, coupons, and read-only app users.",
    permissions: [
      PERMISSIONS.USERS_READ,
      PERMISSIONS.SUPPORT_TICKETS,
      PERMISSIONS.COUPONS,
    ],
    assignableBy: [ROLE_IDS.SUPER_ADMIN, ROLE_IDS.ADMIN],
  },
  [ROLE_IDS.SEO]: {
    label: "SEO",
    shortLabel: "SEO",
    description:
      "EzSignly and Haystackers blogs and blog categories.",
    permissions: [
      PERMISSIONS.BLOGS_EZ,
      PERMISSIONS.BLOG_CATEGORIES_EZ,
      PERMISSIONS.HAYSTACKERS_BLOGS,
      PERMISSIONS.HAYSTACKERS_BLOG_CATEGORIES,
    ],
    assignableBy: [ROLE_IDS.SUPER_ADMIN, ROLE_IDS.ADMIN],
  },
};

/** First screen after login when `/dashboard` is not allowed for the role */
export function getPostLoginPath(role) {
  if (role === ROLE_IDS.SUPPORT) return "/dashboard/support-tickets";
  if (role === ROLE_IDS.SEO) return "/dashboard/blogs";
  return "/dashboard";
}

/** Roles that may be assigned when creating/editing system users (excluding SUPER_ADMIN from dropdown for non–super-admins in UI). */
export const ASSIGNABLE_ROLES_ORDER = [
  ROLE_IDS.ADMIN,
  ROLE_IDS.SUPPORT,
  ROLE_IDS.SEO,
];

/**
 * @param {string | undefined} creatorRole
 * @returns {{ value: AdminRoleId, label: string, description: string }[]}
 */
export function getAssignableRoleOptions(creatorRole) {
  const creator = creatorRole || "";
  if (creator === ROLE_IDS.SUPER_ADMIN) {
    return [
      {
        value: ROLE_IDS.ADMIN,
        label: ROLE_CONFIG[ROLE_IDS.ADMIN].label,
        description: ROLE_CONFIG[ROLE_IDS.ADMIN].description,
      },
      {
        value: ROLE_IDS.SUPPORT,
        label: ROLE_CONFIG[ROLE_IDS.SUPPORT].label,
        description: ROLE_CONFIG[ROLE_IDS.SUPPORT].description,
      },
      {
        value: ROLE_IDS.SEO,
        label: ROLE_CONFIG[ROLE_IDS.SEO].label,
        description: ROLE_CONFIG[ROLE_IDS.SEO].description,
      },
    ];
  }
  if (creator === ROLE_IDS.ADMIN) {
    return [
      {
        value: ROLE_IDS.ADMIN,
        label: ROLE_CONFIG[ROLE_IDS.ADMIN].label,
        description: ROLE_CONFIG[ROLE_IDS.ADMIN].description,
      },
      {
        value: ROLE_IDS.SUPPORT,
        label: ROLE_CONFIG[ROLE_IDS.SUPPORT].label,
        description: ROLE_CONFIG[ROLE_IDS.SUPPORT].description,
      },
      {
        value: ROLE_IDS.SEO,
        label: ROLE_CONFIG[ROLE_IDS.SEO].label,
        description: ROLE_CONFIG[ROLE_IDS.SEO].description,
      },
    ];
  }
  return [];
}

/**
 * @param {string | undefined} role
 * @param {string} permissionKey
 */
export function roleHasPermission(role, permissionKey) {
  if (!role || !ROLE_CONFIG[role]) return false;
  return ROLE_CONFIG[role].permissions.includes(permissionKey);
}

/**
 * Sidebar / route guard: map nav `path` to permission key(s).
 * @param {string} path
 * @returns {string | null} permission key or null = always allow if authenticated
 */
export function pathToPermission(path) {
  if (path === "/dashboard") return PERMISSIONS.DASHBOARD;
  if (path.startsWith("/dashboard/users")) return PERMISSIONS.USERS_READ;
  if (path.startsWith("/dashboard/organizations"))
    return PERMISSIONS.ORGANIZATIONS;
  if (path.startsWith("/dashboard/companies")) return PERMISSIONS.COMPANIES;
  if (path.startsWith("/dashboard/blogs")) return PERMISSIONS.BLOGS_EZ;
  if (path.startsWith("/dashboard/blog-categories"))
    return PERMISSIONS.BLOG_CATEGORIES_EZ;
  if (
    path.startsWith("/dashboard/haystackers-blogs") ||
    path.startsWith("/dashboard/haystackers-blogs-details")
  )
    return PERMISSIONS.HAYSTACKERS_BLOGS;
  if (path.startsWith("/dashboard/haystackers-blog-categories"))
    return PERMISSIONS.HAYSTACKERS_BLOG_CATEGORIES;
  if (path.startsWith("/dashboard/system-users"))
    return PERMISSIONS.SYSTEM_USERS;
  if (path.startsWith("/dashboard/support-tickets"))
    return PERMISSIONS.SUPPORT_TICKETS;
  if (path.startsWith("/dashboard/coupons")) return PERMISSIONS.COUPONS;
  if (path.startsWith("/dashboard/migrations")) return PERMISSIONS.MIGRATIONS;
  return null;
}

/**
 * @param {string | undefined} role
 * @param {string} path
 */
export function canAccessPath(role, path) {
  const perm = pathToPermission(path);
  if (perm === null) return true;
  return roleHasPermission(role, perm);
}

/**
 * @param {string | undefined} role
 */
export function getRoleBadgeClass(role) {
  switch (role) {
    case ROLE_IDS.SUPER_ADMIN:
      return "bg-amber-100 text-amber-900";
    case ROLE_IDS.ADMIN:
      return "bg-purple-100 text-purple-800";
    case ROLE_IDS.SUPPORT:
      return "bg-blue-100 text-blue-800";
    case ROLE_IDS.SEO:
      return "bg-emerald-100 text-emerald-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}
